import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { JsonConfiguration } from '../../../shared/total-price-resume/json';
import moment, { Moment } from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';
import { ScreenDeviceService } from '../../../core/services/Screen/screen-device.service';
import { Subject, takeUntil } from 'rxjs';
import { HeaderConfiguration } from '../../../shared/header/header.component';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { AnalyticsService } from '../../../core/services/adobe-analytics/analytics.service';
import { environment } from '../../../../environments/environment';
import { Utils } from '../../../shared/utils/utils';

const DATE_FORMAT_MMYYYY = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  providers: [
    JsonConfiguration,
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT_MMYYYY }
  ]
})
export class PaymentComponent implements OnInit {

  @Output() complete = new EventEmitter<void>();
  @Output() formDataPayment = new EventEmitter<void>();
  @Output() onPay = new EventEmitter<void>();
  @Output() backStep = new EventEmitter<number>();

  @Input() formDataPaymentReceived: FormGroup;
  @Input() htmlConsents: string = '';

  destroy$ = new Subject<void>();
  isDesktopScreen: boolean = false;
  paymentForm!: FormGroup;
  whiteHandIcon: string = 'assets/img/Mano-blanca.svg';
  checked: boolean = false;
  startDate = new Date();
  minDate = new Date();
  maxDate = new Date();
  headerConfiguration: HeaderConfiguration;
  selectExpiryDate: string = '';
  step: number = 2;
  isContinueDisabled: boolean = true;
  policySummary: any = null;

  constructor(
    private fb: FormBuilder,
    private screenDeviceService: ScreenDeviceService,
    private cdRef: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
  ) { }

  ngOnInit(): void {
    this.analyticsService.resetDataLayer();
    this.checkScreen();
    this.set_header_configuration();
    this.minDate = moment().startOf("month").toDate();
    this.maxDate = moment().add(10, 'years').endOf('year').toDate();

    this.createForm();

    const stored = sessionStorage.getItem('policySummary');
    if (stored) {
      this.policySummary = JSON.parse(stored);
    }

    this.paymentForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.isContinueDisabled = status !== 'VALID';
        this.cdRef.detectChanges();
      });

    this.set_initial_analytics_config('click-button:payment:abrir_pagina_payment',
      'imPageview',
      'app_cotizador:area_publica:sales:creditcardPayment',
      'creditcardPayment',
      'sales_creditcardPayment',
      '13',
      'resumen');

    this.analyticsService.startEventWithCallback('imPageview', () => { });
  }

  private createForm() {
    this.paymentForm = this.fb.group({
      cardNumber: ['', [Validators.required, CustomValidators.checkCreditCard(),]],
      expiryDate: ['', [Validators.required]],
      cvv: ['', [Validators.required, Validators.pattern(/^\d{3}$/)]],
      termsAccepted: ['', [Validators.requiredTrue]]
    });
  }

  onSubmit(): void {
    if (this.paymentForm.valid) {
      this.set_initial_analytics_config('click-button:contratar',
        'SalesP3',
        'app_cotizador:area_publica:sales:creditcardPayment',
        'yourInsurance',
        'sales_yourInsurance',
        '10',
        'iniciado');

      this.analyticsService.startEventWithCallback('SalesP3', () => {
        this.formDataPayment.emit(this.paymentForm.value);
        this.complete.emit();
      });

    } else {
      this.paymentForm.markAllAsTouched();
    }
  }
  checkScreen() {
    this.screenDeviceService.checkScreenSize()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.isDesktopScreen = result.matches;
      });
  }

  private set_header_configuration() {
    this.headerConfiguration = {
      layoutAlign: 'start start',
      sections: [
        {
          backButton: {
            styles: ['fs_4_underline']
          },
        },
      ],
    };
  }

  handleBackClick() {
    this.backStep.emit(this.step);
  }

  chosenYearHandler(normalizedYear: Moment) {
    this.selectExpiryDate = '';
    this.selectExpiryDate += normalizedYear.year().toString() + "-";
  }

  chosenMonthHandler(normalizedMonth: Moment, datepicker: MatDatepicker<Moment>) {
    this.selectExpiryDate += (normalizedMonth.month() + 1).toString().padStart(2, "0");
    this.paymentForm.controls['expiryDate'].setValue(this.selectExpiryDate);
    datepicker.close();
  }


  onCardNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = (input.value || '').replace(/\D+/g, '');

    if (input.value !== cleaned) {
      input.value = cleaned;
    }

    const ctrl = this.paymentForm.get('cardNumber');
    if (ctrl && ctrl.value !== cleaned) {
      ctrl.setValue(cleaned, { emitEvent: true });
    }
  }

  onCardNumberPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const paste = (event.clipboardData?.getData('text') || '');
    const cleaned = paste.replace(/\D+/g, '');
    const input = event.target as HTMLInputElement;
    input.value = (input.value + cleaned).slice(0, 19);
    const ctrl = this.paymentForm.get('cardNumber');
    if (ctrl) {
      ctrl.setValue(input.value, { emitEvent: true });
    }
  }

  onCardKeyPress(event: KeyboardEvent) {
    const allowed = /[0-9]/;
    if (!allowed.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
    * Set all the analytics' related info for the current component
  */
  private set_initial_analytics_config(categoryActionLabel: string,
    eventName: string,
    pageName: string, familySelection:
      string, processName: string,
    processStep: string,
    processStatus: string): void {
    const sessionData = this.getAnalyticsDataFromSession();
    this.analyticsService.resetDataLayer();
    /* ===================== */
    /* PAGE DATA */
    /* ===================== */
    this.analyticsService.setPhysicalUrl(environment.baseUrl + sessionData.aliasProducto);
    this.analyticsService.setPageName(pageName);
    this.analyticsService.setPageContentType('cotizador');
    this.analyticsService.setPageDigitalChannel('app_cotizador');
    this.analyticsService.setPageSection('area_publica');
    this.analyticsService.setPageLanguage('es');
    this.analyticsService.setNivel1('sales');
    this.analyticsService.setNivel2(familySelection);
    /* ===================== */
    /* PROCESS DATA */
    /* ===================== */
    this.analyticsService.setProcessStep(processStep);
    this.analyticsService.setProcessType('sales');
    this.analyticsService.setProcessName(processName);
    this.analyticsService.setProcessStatus(processStatus);

    /* ===================== */
    /* USER DATA */
    /* ===================== */
    this.analyticsService.setUserStatus('no_logado');
    this.analyticsService.setUserLang('es');
    const personalFormData = sessionStorage.getItem('personalForm');
    let userBirth = '';
    if (personalFormData) {
      try {
        const parsed = JSON.parse(personalFormData);
        userBirth = parsed.fechaNacimiento ?? '';
      } catch (err) {
        console.warn('Error parsing personalForm from sessionStorage', err);
      }
    }

    this.analyticsService.setUserBirth(userBirth);
    this.analyticsService.setUserResidence('VERDADERO');

    /* ===================== */
    /* PRODUCT DATA (DINÁMICO) */
    /* ===================== */

    this.analyticsService.setFamilySelection(familySelection);
    this.analyticsService.setCategoryActionLabel(categoryActionLabel);
    this.analyticsService.setProductName(Utils.sanitizeString(sessionData.productName));
    this.analyticsService.setProductType(Utils.sanitizeString(sessionData.productType));
    this.analyticsService.setProductArea(sessionData.productArea);
    this.analyticsService.setProductDate(Utils.getProductDate());
    this.analyticsService.setProductPack('todo el año')

    const productCostStart = sessionData.productCostStart;
    const productCostUpgrades = sessionData.productCostUpgrades;
    this.analyticsService.setProductCostStart(productCostStart);
    this.analyticsService.setProductCostUpgrades(productCostUpgrades);

    /* ===================== */
    /* PAYMENT */
    /* ===================== */

    const quotationId = sessionStorage.getItem('quotationId') ?? '';
    this.analyticsService.setIDPresupuesto(quotationId);
    this.analyticsService.setProductPayment('Anual (' + sessionData.productCostStart + ' €/año)');
    this.analyticsService.setProductPaymentType('Cliente con Tarjeta');

  }

  private getAnalyticsDataFromSession(): any {
    const stored = sessionStorage.getItem('analyticsData');
    if (!stored) return null;
    return JSON.parse(stored);
  }

}
